import { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../services/apiService';
import { AudioRecorder } from '../utils/audioRecorder';
import { ensureRecordAudioSession } from '../utils/audioUnlock';
import { recorderFilenameForMime } from '../utils/recorderMimeType';
import { watchForSpeechSegment } from '../utils/silenceMonitor';
import {
  beginGetUserMediaPermission,
  getUserMediaConstraints,
  isIOSDevice,
  isStreamLive,
  markMicrophonePermissionGranted,
  refreshMicrophonePermissionState,
  releaseMicrophoneWarmupStream,
  speechInputErrorCodeFromGetUserMedia,
} from '../utils/micPermission';

function minRecordedBlobBytes() {
  return isIOSDevice() ? 500 : 800;
}

/** Keep recording briefly after release so trailing syllables are not clipped. */
function holdStopTailMs() {
  return isIOSDevice() ? 420 : 320;
}

function silenceMonitorOptionsForPlatform() {
  if (isIOSDevice()) {
    return {
      calibrateMs: 400,
      silenceMs: 1400,
      minSpeechMs: 350,
      maxWaitForSpeechMs: 18000,
      speechMultiplier: 3.1,
      silenceMultiplier: 1.8,
    };
  }
  return {
    calibrateMs: 300,
    silenceMs: 1100,
    minSpeechMs: 280,
    maxWaitForSpeechMs: 15000,
    speechMultiplier: 3.4,
    silenceMultiplier: 1.65,
  };
}

/** BCP-47 locale from app language code. */
export function speechLocaleForAppLanguage(lang) {
  if (lang === 'el') return 'el-GR';
  return 'en-US';
}

function appLanguageFromLocale(locale) {
  if ((locale || '').toLowerCase().startsWith('el')) return 'el';
  return 'en';
}

function mapGetUserMediaSpeechError(err) {
  return speechInputErrorCodeFromGetUserMedia(err);
}

/**
 * Server speech-to-text for the chat composer (hold-to-record).
 * All browsers: MediaRecorder + POST /api/stt when the backend has STT configured.
 * Recording starts on pointer down and stops (then transcribes) on pointer up.
 */
export function useSpeechInput({ locale, onResult, onError, disabled, cancelCaptureOnDisable = false }) {
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [supported, setSupported] = useState(false);
  const [usesServerStt, setUsesServerStt] = useState(false);
  const recorderRef = useRef(null);
  const useRecorderPathRef = useRef(false);
  const transcribeGenerationRef = useRef(0);
  const silenceMonitorCleanupRef = useRef(null);
  const pendingStreamRef = useRef(null);
  const activeStreamRef = useRef(null);
  const stopRecorderRef = useRef(null);
  const isTranscribingRef = useRef(false);
  const iosSilencePollRef = useRef(null);
  const iosUtteranceEndingRef = useRef(false);
  const holdModeRef = useRef(false);
  const holdWantedRef = useRef(false);
  const holdGenerationRef = useRef(0);
  const holdStopTailTimerRef = useRef(null);
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  const disabledRef = useRef(disabled);
  /** Skip the disabled→stop effect once after a forced start from a user tap. */
  const ignoreDisabledStopRef = useRef(false);

  onResultRef.current = onResult;
  onErrorRef.current = onError;
  disabledRef.current = disabled;

  useEffect(() => {
    refreshMicrophonePermissionState();

    const hasRecorderApi =
      typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);

    if (!hasRecorderApi) {
      useRecorderPathRef.current = false;
      setUsesServerStt(false);
      setSupported(false);
      return;
    }

    void apiService.getSttStatus().then((status) => {
      if (status.enabled) {
        useRecorderPathRef.current = true;
        setUsesServerStt(true);
        setSupported(true);
        return;
      }
      useRecorderPathRef.current = false;
      setUsesServerStt(false);
      setSupported(false);
    });
  }, []);

  const releaseActiveStream = useCallback(() => {
    if (!activeStreamRef.current) return;
    for (const track of activeStreamRef.current.getTracks()) {
      track.stop();
    }
    activeStreamRef.current = null;
    pendingStreamRef.current = null;
  }, []);

  const stopSilenceMonitor = useCallback(() => {
    if (iosSilencePollRef.current) {
      clearInterval(iosSilencePollRef.current);
      iosSilencePollRef.current = null;
    }
    if (silenceMonitorCleanupRef.current) {
      silenceMonitorCleanupRef.current();
      silenceMonitorCleanupRef.current = null;
    }
  }, []);

  const setTranscribing = useCallback((value) => {
    isTranscribingRef.current = value;
    setIsTranscribing(value);
  }, []);

  const clearHoldStopTailTimer = useCallback(() => {
    if (holdStopTailTimerRef.current) {
      clearTimeout(holdStopTailTimerRef.current);
      holdStopTailTimerRef.current = null;
    }
  }, []);

  const stop = useCallback((options = {}) => {
    const { transcribe = false, cancelTranscription = false, resumeAfter = false } = options;
    holdWantedRef.current = false;
    stopSilenceMonitor();

    if (recorderRef.current?.isRecording) {
      const shouldTailHold = transcribe && holdModeRef.current && !cancelTranscription;
      if (shouldTailHold) {
        clearHoldStopTailTimer();
        holdStopTailTimerRef.current = window.setTimeout(() => {
          holdStopTailTimerRef.current = null;
          void stopRecorderRef.current?.({ transcribe, resumeAfter });
        }, holdStopTailMs());
        return;
      }
      clearHoldStopTailTimer();
      void stopRecorderRef.current?.({ transcribe, resumeAfter });
      return;
    }
    clearHoldStopTailTimer();

    if (cancelTranscription || !isTranscribingRef.current) {
      releaseActiveStream();
    }
    releaseMicrophoneWarmupStream();

    if (cancelTranscription) {
      transcribeGenerationRef.current += 1;
      setTranscribing(false);
    } else if (!isTranscribingRef.current) {
      transcribeGenerationRef.current += 1;
    }
    recorderRef.current?.abort();
    recorderRef.current = null;
    setIsListening(false);
  }, [stopSilenceMonitor, releaseActiveStream, setTranscribing, clearHoldStopTailTimer]);

  const armIosRecordingSession = useCallback(
    async (stream) => {
      if (!stream || !useRecorderPathRef.current) return;

      ensureRecordAudioSession();
      stopSilenceMonitor();
      iosUtteranceEndingRef.current = false;

      const finishUtterance = () => {
        if (iosUtteranceEndingRef.current || !recorderRef.current?.isRecording) {
          return;
        }
        iosUtteranceEndingRef.current = true;
        void stopRecorderRef.current?.({ transcribe: true, resumeAfter: true });
      };

      try {
        if (!recorderRef.current) {
          recorderRef.current = new AudioRecorder();
        }
        if (!recorderRef.current.isRecording) {
          pendingStreamRef.current = null;
          await recorderRef.current.start(stream);
        }

        silenceMonitorCleanupRef.current = watchForSpeechSegment(
          stream,
          {
            onSpeechEnd: finishUtterance,
          },
          {
            ...silenceMonitorOptionsForPlatform(),
            detectEndOnly: true,
          },
        );

        const startedAt = Date.now();
        const maxRecordMs = 30000;
        iosSilencePollRef.current = window.setInterval(() => {
          const recorder = recorderRef.current;
          if (!recorder?.isRecording) {
            stopSilenceMonitor();
            return;
          }
          if (Date.now() - startedAt >= maxRecordMs) {
            finishUtterance();
            return;
          }
          if (recorder.detectUtteranceEndFromChunks()) {
            finishUtterance();
          }
        }, 150);
      } catch (err) {
        onErrorRef.current?.(mapGetUserMediaSpeechError(err));
        stop();
      }
    },
    [stop, stopSilenceMonitor],
  );

  const armSpeechWatcher = useCallback(
    (stream) => {
      if (!stream || !useRecorderPathRef.current) return;
      void armIosRecordingSession(stream);
    },
    [armIosRecordingSession],
  );

  const resumeListening = useCallback(
    (stream) => {
      if (!stream || !isStreamLive(stream) || disabledRef.current) return false;
      pendingStreamRef.current = stream;
      activeStreamRef.current = stream;
      setIsListening(true);
      armSpeechWatcher(stream);
      return true;
    },
    [armSpeechWatcher],
  );

  const stopRecorder = useCallback((options = {}) => {
    const { transcribe = true, resumeAfter = false } = options;
    clearHoldStopTailTimer();
    stopSilenceMonitor();
    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (!recorder) {
      setIsListening(false);
      if (!resumeAfter) {
        releaseActiveStream();
      }
      return Promise.resolve();
    }
    if (!recorder.isRecording) {
      recorder.abort();
      setIsListening(false);
      if (!resumeAfter) {
        releaseActiveStream();
      }
      return Promise.resolve();
    }

    if (transcribe) {
      setTranscribing(true);
    }
    setIsListening(false);

    const generation = transcribeGenerationRef.current;
    const maybeResume = () => {
      if (!resumeAfter) {
        releaseActiveStream();
        return;
      }
      if (activeStreamRef.current && !disabledRef.current) {
        resumeListening(activeStreamRef.current);
      }
    };

    return recorder
      .stop()
      .then(async (blob) => {
        const minBytes = minRecordedBlobBytes();
        if (!transcribe || !blob || blob.size < minBytes) {
          setTranscribing(false);
          maybeResume();
          return;
        }
        try {
          const language = appLanguageFromLocale(locale);
          const namedBlob = new File(
            [blob],
            recorderFilenameForMime(blob.type),
            { type: blob.type || 'audio/webm' },
          );
          const text = await apiService.speechToText(namedBlob, language);
          if (generation !== transcribeGenerationRef.current) return;
          const trimmed = (text || '').trim();
          if (trimmed) {
            onResultRef.current?.(trimmed);
          }
        } catch (err) {
          if (generation === transcribeGenerationRef.current) {
            onErrorRef.current?.(err?.code || 'transcription-failed');
          }
        } finally {
          if (generation === transcribeGenerationRef.current) {
            setTranscribing(false);
            maybeResume();
          }
        }
      })
      .catch(() => {
        setIsListening(false);
        setTranscribing(false);
        if (!resumeAfter) {
          releaseActiveStream();
        }
        onErrorRef.current?.('recording-failed');
      });
  }, [locale, stopSilenceMonitor, setTranscribing, resumeListening, releaseActiveStream, clearHoldStopTailTimer]);

  stopRecorderRef.current = stopRecorder;

  const startDirectRecorder = useCallback(
    async (stream, generation) => {
      if (!stream || !isStreamLive(stream)) return;
      if (generation !== holdGenerationRef.current || !holdWantedRef.current) {
        for (const track of stream.getTracks()) {
          track.stop();
        }
        return;
      }
      ensureRecordAudioSession();
      stopSilenceMonitor();
      recorderRef.current?.abort();
      const recorder = new AudioRecorder();
      recorderRef.current = recorder;
      activeStreamRef.current = stream;
      pendingStreamRef.current = stream;
      try {
        await recorder.start(stream);
        if (generation !== holdGenerationRef.current || !holdWantedRef.current) {
          recorderRef.current = null;
          recorder.abort();
          return;
        }
        setIsListening(true);
      } catch (err) {
        recorderRef.current = null;
        releaseActiveStream();
        onErrorRef.current?.(mapGetUserMediaSpeechError(err));
      }
    },
    [stopSilenceMonitor, releaseActiveStream],
  );

  const startRecorder = useCallback(
    async (existingStream = null, options = {}) => {
      const { holdMode = false, generation = holdGenerationRef.current } = options;
      ensureRecordAudioSession();
      stopSilenceMonitor();
      recorderRef.current?.abort();
      recorderRef.current = null;

      const reuseStream =
        existingStream && isStreamLive(existingStream)
          ? existingStream
          : activeStreamRef.current && isStreamLive(activeStreamRef.current)
            ? activeStreamRef.current
            : null;

      if (reuseStream) {
        if (holdMode && useRecorderPathRef.current) {
          await startDirectRecorder(reuseStream, generation);
          return;
        }
        activeStreamRef.current = reuseStream;
        pendingStreamRef.current = reuseStream;
        setIsListening(true);
        if (useRecorderPathRef.current) {
          armSpeechWatcher(reuseStream);
        }
        return;
      }

      releaseActiveStream();
      try {
        const stream = await navigator.mediaDevices.getUserMedia(getUserMediaConstraints());
        activeStreamRef.current = stream;
        pendingStreamRef.current = stream;

        if (holdMode) {
          await startDirectRecorder(stream, generation);
          return;
        }

        setIsListening(true);
        armSpeechWatcher(stream);
      } catch (err) {
        releaseActiveStream();
        onErrorRef.current?.(mapGetUserMediaSpeechError(err));
      }
    },
    [stopSilenceMonitor, releaseActiveStream, armSpeechWatcher, startDirectRecorder],
  );

  const beginHoldSession = useCallback((holdMode) => {
    holdGenerationRef.current += 1;
    holdWantedRef.current = true;
    holdModeRef.current = holdMode;
    return holdGenerationRef.current;
  }, []);

  const startRecorderPath = useCallback(
    (options = {}) => {
      const { force = false, holdMode = true } = options;
      if (!force && disabledRef.current) return;
      if (!supported && !force) return;

      if (recorderRef.current?.isRecording) {
        return;
      }

      const generation = beginHoldSession(holdMode);
      if (force) {
        ignoreDisabledStopRef.current = true;
      }

      ensureRecordAudioSession();

      if (recorderRef.current) {
        recorderRef.current.abort();
        recorderRef.current = null;
      }

      if (activeStreamRef.current && isStreamLive(activeStreamRef.current)) {
        void startRecorder(activeStreamRef.current, { holdMode, generation });
        return;
      }

      const streamPromise = beginGetUserMediaPermission();
      if (!streamPromise) {
        onErrorRef.current?.('unsupported');
        return;
      }

      streamPromise
        .then((stream) => {
          if (generation !== holdGenerationRef.current || !holdWantedRef.current) {
            for (const track of stream.getTracks()) {
              track.stop();
            }
            return;
          }
          markMicrophonePermissionGranted();
          return startRecorder(stream, { holdMode, generation });
        })
        .catch((err) => {
          if (generation !== holdGenerationRef.current) return;
          onErrorRef.current?.(mapGetUserMediaSpeechError(err));
        });
    },
    [supported, startRecorder, beginHoldSession],
  );

  const start = useCallback(
    (options = {}) => {
      const { holdMode = true, ...rest } = options;
      if (!useRecorderPathRef.current) {
        onErrorRef.current?.('unsupported');
        return;
      }
      startRecorderPath({ ...rest, holdMode });
    },
    [startRecorderPath],
  );

  useEffect(() => {
    if (disabled && isListening) {
      if (ignoreDisabledStopRef.current) return;
      ignoreDisabledStopRef.current = false;
      stop({
        transcribe: cancelCaptureOnDisable
          ? false
          : Boolean(recorderRef.current?.isRecording),
        cancelTranscription: cancelCaptureOnDisable,
        resumeAfter: false,
      });
      return;
    }
    if (ignoreDisabledStopRef.current && !disabled) {
      ignoreDisabledStopRef.current = false;
    }
  }, [disabled, isListening, stop, cancelCaptureOnDisable]);

  useEffect(() => () => stop({ cancelTranscription: true }), [stop]);

  return { isListening, isTranscribing, supported, usesServerStt, start, stop };
}
